import { FC } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { PickupDeliveryLocationResponseV1 } from '@forto-dev/customer-master-data-models'
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToaster,
} from '@forto-dev/phoenix-ui-core'

import { useLocalisation } from '@/utils/localisation'

import { LocationForm } from './LocationForm'
import { usePickupDeliveryLocationCreateMutation } from '../../api/pickupDeliveryLocation.service'
import { LocationFormData, locationFormDataSchema } from '../../schema'
import { mapLocationFormToPickupDeliveryLocationCreateRequest } from '../../utils/locationFormMapper'

type LocationAddDialogProps = {
  accountID: string
  organizationID?: string
  onClose: (newLocation?: PickupDeliveryLocationResponseV1) => void
  title?: string
  description?: string
}

export const LocationAddDialog: FC<LocationAddDialogProps> = (props) => {
  const toaster = useToaster()
  const { translate } = useLocalisation()
  const {
    accountID,
    organizationID,
    onClose,
    title = translate('locations.add.title'),
    description = translate('locations.add.description'),
  } = props

  const defaultValues = {
    address: null,
    locationName: '',
    notes: '',
    companyName: '',
    contact: [],
    unLocode: '',
  }

  const formMethods = useForm<LocationFormData>({
    defaultValues,
    resolver: zodResolver(locationFormDataSchema),
  })

  const { mutate: create, isPending: isSubmitting } = usePickupDeliveryLocationCreateMutation()

  const onSave = (formData: LocationFormData) => {
    const request = mapLocationFormToPickupDeliveryLocationCreateRequest(accountID, formData, organizationID)

    create(request, {
      onSuccess: (newLocation: PickupDeliveryLocationResponseV1) => {
        toaster({ variant: 'positive', title: translate('locations.add.success') })
        onClose(newLocation)
      },
      onError: (error) => {
        toaster({
          variant: 'negative',
          title: translate('locations.add.error'),
          description: error.message,
        })
      },
    })
  }

  return (
    <Dialog
      defaultOpen
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <FormProvider {...formMethods}>
            <LocationForm />
          </FormProvider>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline-primary">{translate('actions.cancel')}</Button>
          </DialogClose>
          <Button variant="primary" onClick={formMethods.handleSubmit(onSave)} disabled={isSubmitting}>
            {isSubmitting ? translate('locations.saving') : translate('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
